import React, { useState, useRef, useEffect, useCallback } from 'react';

/**
 * ThumbnailStrip component displays clickable thumbnails for video navigation
 * 
 * Features:
 * - Horizontal scrollable thumbnail strip
 * - Click to seek video to specific time
 * - Active thumbnail highlighting
 * - Smooth scrolling to active thumbnail
 * - Responsive design for mobile devices
 */
const ThumbnailStrip = ({ thumbnails, onThumbnailClick }) => {
  const [activeThumbnail, setActiveThumbnail] = useState(0);
  const stripRef = useRef(null);

  /**
   * Handle thumbnail click
   * Updates active state and calls parent callback
   */
  const handleThumbnailClick = (index, time) => {
    setActiveThumbnail(index);
    onThumbnailClick(time);
  };

  /**
   * Scroll to active thumbnail
   * Ensures the active thumbnail is visible in the viewport
   */
  const scrollToActiveThumbnail = useCallback((index) => {
    if (stripRef.current) {
      const thumbnailWidth = 90; // 80px width + 10px gap
      const scrollPosition = index * thumbnailWidth - (stripRef.current.clientWidth / 2) + (thumbnailWidth / 2);
      
      stripRef.current.scrollTo({
        left: Math.max(0, scrollPosition),
        behavior: 'smooth'
      });
    }
  }, []);

  /**
   * Update active thumbnail when thumbnails change
   */
  useEffect(() => {
    if (thumbnails.length > 0) {
      setActiveThumbnail(0);
    }
  }, [thumbnails]);

  /**
   * Handle keyboard navigation
   */
  const handleKeyDown = (event, index) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleThumbnailClick(index, thumbnails[index].time);
    }
  };

  if (thumbnails.length === 0) {
    return null;
  }

  return (
    <div className="thumbnail-container">
      <h3>Timeline Navigation</h3>
      <p className="thumbnail-instructions">
        Click on any thumbnail to jump to that time in the video
      </p>
      
      <div 
        ref={stripRef}
        className="thumbnail-strip"
        role="tablist"
        aria-label="Video timeline thumbnails"
      >
        {thumbnails.map((thumbnail, index) => (
          <div
            key={index}
            className={`thumbnail-item ${activeThumbnail === index ? 'active' : ''}`}
            onClick={() => handleThumbnailClick(index, thumbnail.time)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            tabIndex={0}
            role="tab"
            aria-label={`Jump to ${thumbnail.timestamp}`}
            title={`Jump to ${thumbnail.timestamp}`}
          >
            <img
              src={thumbnail.dataUrl}
              alt={`Video frame at ${thumbnail.timestamp}`}
              className="thumbnail-image"
              loading="lazy"
            />
            <div className="thumbnail-time">
              {thumbnail.timestamp}
            </div>
          </div>
        ))}
      </div>
      
      {/* Navigation hints */}
      <div className="thumbnail-hints">
        <small>
          💡 Tip: Use the scroll wheel or drag to navigate through thumbnails
        </small>
      </div>
    </div>
  );
};

export default ThumbnailStrip;

